import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Caption } from '@sorare/core/src/atoms/typography';
import { NotIOsAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { ANY_SPORT_PLAYERS_SLUG_CARDS } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useAmountWithConversion from '@sorare/core/src/hooks/useAmountWithConversion';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useStoreAmount } from '@sorare/core/src/hooks/useStoreAmount';
import { glossary } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import { ClickInstantBuy_Flow } from '@sorare/core/src/protos/events/so5/shared/events';

import { AnyPlayerStatsIcon } from 'components/player/AnyPlayerStatsIcon';
import PrimaryBuyBuyField from 'components/primaryBuy/PrimaryBuyBuyField';
import { useIsPrimaryBuyLive } from 'hooks/primaryBuy/useIsPrimaryBuyLive';

import { tileCss } from '../ui';
import { Header } from './Header';
import { LastFifteen } from './LastFifteen';
import { LastGame } from './LastGame';
import {
  Recommendation_anyCard,
  Recommendation_anyPlayer,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  ${tileCss}
  background-color: var(--c-neutral-200);
`;

const Left = styled.div``;
const Right = styled(Horizontal)`
  align-items: flex-end;
`;

const Bottom = styled(Vertical)`
  width: 100%;
`;
const Row = styled(Horizontal).attrs({ gap: 1 })`
  width: 100%;
  justify-content: space-between;
  align-items: flex-end;
`;

const Secondary = styled.span`
  color: var(--c-neutral-600);
  font-weight: 400 !important;
  & > span {
    font-weight: 400;
  }
`;

type Props = {
  children: ReactNode;
  player: Recommendation_anyPlayer;
  card: Nullable<Recommendation_anyCard>;
  chip?: ReactNode;
  onStatsClick: () => void;
  trackForYouClick: () => void;
  followRefetch: () => void;
  subscribable: Recommendation_anyPlayer | Recommendation_anyCard;
};

export const Recommendation = ({
  children,
  player,
  card,
  chip,
  onStatsClick,
  followRefetch,
  subscribable,
  trackForYouClick,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { up: isTablet } = useScreenSize('tablet');
  const isPrimaryBuyLive = useIsPrimaryBuyLive(card);
  const { main, exponent } = useAmountWithConversion({
    monetaryAmount: card?.latestPrimaryOffer?.price || zeroMonetaryAmount,
  });

  const { storeMain, storeExponent } = useStoreAmount({
    monetaryAmount: card?.latestPrimaryOffer?.price || zeroMonetaryAmount,
    googlePrice: card?.latestPrimaryOffer?.googlePrice,
    applePrice: card?.latestPrimaryOffer?.applePrice,
  });

  return (
    <Root>
      <Header
        subscribable={subscribable}
        chip={chip}
        refetch={followRefetch}
        playerSlug={player.slug}
        trackForYouClick={trackForYouClick}
      />
      {children}
      <Bottom gap={2}>
        <Row>
          <Left>
            <LastGame player={player} />
          </Left>
          <Right>
            <button
              type="button"
              onClick={() => {
                onStatsClick();
                trackForYouClick();
              }}
            >
              <AnyPlayerStatsIcon player={player} />
            </button>
            <LastFifteen player={player} />
          </Right>
        </Row>
        {(card && isPrimaryBuyLive) || !card ? (
          <Horizontal center>
            {card && isPrimaryBuyLive && (
              <PrimaryBuyBuyField
                card={card}
                color="tertiary"
                size="medium"
                fullWidth
                onClick={trackForYouClick}
                cta={
                  <FormattedMessage
                    {...glossary.primaryBuy}
                    values={{
                      amount: (
                        <Secondary>
                          {storeMain || main}
                          {(storeExponent || exponent) && isTablet ? (
                            <Caption as="span">
                              ({storeExponent || exponent})
                            </Caption>
                          ) : (
                            ''
                          )}
                        </Secondary>
                      ),
                    }}
                  />
                }
                origin={ClickInstantBuy_Flow.FOR_YOU}
              />
            )}
            {!card && (
              <Button size="medium" disabled color="quaternary" fullWidth>
                <FormattedMessage
                  id="primaryBuy.sold"
                  defaultMessage="Sold Out"
                />
              </Button>
            )}
          </Horizontal>
        ) : (
          <NotIOsAppFeature>
            <Button
              size="medium"
              color="tertiary"
              onClick={trackForYouClick}
              fullWidth
              to={`${generateSportContextPath(ANY_SPORT_PLAYERS_SLUG_CARDS, {
                params: {
                  slug: card?.anyPlayer.slug,
                },
              })}?${buildFilterQuery({
                [SEARCH_PARAMS.RARITY]: card?.rarityTyped,
                [SEARCH_PARAMS.ON_SALE]: true,
              })}`}
            >
              <FormattedMessage
                id="primaryBuy.ScoutMarket"
                defaultMessage="Scout market"
              />
            </Button>
          </NotIOsAppFeature>
        )}
      </Bottom>
    </Root>
  );
};

Recommendation.fragments = {
  anyCard: gql`
    fragment Recommendation_anyCard on AnyCardInterface {
      slug
      latestPrimaryOffer {
        id
        price {
          ...MonetaryAmountFragment_monetaryAmount
        }
        googlePrice(countryCode: $googlePlayStoreCountryCode)
          @include(if: $isAndroidApp)
        applePrice @include(if: $isIosApp) {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
      ...useIsPrimaryBuyLive_anyCard
      ...PrimaryBuyBuyField_anyCard
      ...RecommendationHeader_anyCard
    }
    ${monetaryAmountFragment}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${PrimaryBuyBuyField.fragments.anyCard}
    ${Header.fragments.anyCard}
  ` as TypedDocumentNode<Recommendation_anyCard>,
  anyPlayer: gql`
    fragment Recommendation_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      anyPositions
      sport
      activeClub {
        slug
        name
      }
      ...RecommendationHeader_anyPlayer
      ...AnyPlayerStatsIcon_anyPlayer
      ...RecommendationLastFifteen_anyPlayer
      ...RecommendationLastGame_anyPlayer
    }
    ${Header.fragments.anyPlayer}
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${LastFifteen.fragments.anyPlayer}
    ${LastGame.fragments.anyPlayer}
  ` as TypedDocumentNode<Recommendation_anyPlayer>,
};
